import React, { useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { Popup } from '@atlaskit/popup/popup';
import { Box, Flex, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { ClassificationView } from '../..//common/ui/classification-view';
import { colorsConfig, disabledColorConfig, nameMaxLength } from '../../common/constants';
import { ClassificationIcon } from '../../common/ui/classification-icon';
import { messages } from '../../messages';

import { type DataClassificationLevelProps } from './types';

const styles = cssMap({
	base: {
		display: 'inline-flex',
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		gap: token('space.050'),
		alignItems: 'center',
		justifyContent: 'center',
		whiteSpace: 'normal',
		textOverflow: 'ellipsis',
		cursor: 'default',
		borderRadius: token('radius.small'),
	},
	compact: {
		height: '20px',
	},
	expanded: {
		height: '24px',
	},
	disabled: {
		color: token('color.text.disabled'),
		backgroundColor: token('color.background.disabled'),
		cursor: 'default',
	},
	clickable: {
		cursor: 'pointer',
	},
	red: {
		color: token('color.text'),
		backgroundColor: token('color.background.accent.red.subtler'),
	},
	redClickable: {
		'&:focus-visible': {
			backgroundColor: token('color.background.accent.red.subtler'),
		},
		'&:hover': {
			backgroundColor: token('color.background.accent.red.subtler.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.red.subtler.pressed'),
		},
	},
	redBold: {
		color: token('color.text.inverse'),
		backgroundColor: token('color.background.accent.red.bolder'),
	},
	redBoldClickable: {
		'&:focus-visible': {
			backgroundColor: token('color.background.accent.red.bolder'),
		},
		'&:hover': {
			backgroundColor: token('color.background.accent.red.bolder.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.red.bolder.pressed'),
		},
	},
	orange: {
		color: token('color.text'),
		backgroundColor: token('color.background.accent.orange.subtle'),
	},
	orangeClickable: {
		'&:focus-visible': {
			backgroundColor: token('color.background.accent.orange.subtle'),
		},
		'&:hover': {
			backgroundColor: token('color.background.accent.orange.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.orange.subtle.pressed'),
		},
	},
	yellow: {
		color: token('color.text'),
		backgroundColor: token('color.background.accent.yellow.subtlest'),
	},
	yellowClickable: {
		'&:focus-visible': {
			backgroundColor: token('color.background.accent.yellow.subtlest'),
		},
		'&:hover': {
			backgroundColor: token('color.background.accent.yellow.subtlest.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.yellow.subtlest.pressed'),
		},
	},
	green: {
		color: token('color.text'),
		backgroundColor: token('color.background.accent.green.subtlest'),
	},
	greenClickable: {
		'&:focus-visible': {
			backgroundColor: token('color.background.accent.green.subtlest'),
		},
		'&:hover': {
			backgroundColor: token('color.background.accent.green.subtlest.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.green.subtlest.pressed'),
		},
	},
	blue: {
		color: token('color.text'),
		backgroundColor: token('color.background.accent.blue.subtler'),
	},
	blueClickable: {
		'&:focus-visible': {
			backgroundColor: token('color.background.accent.blue.subtler'),
		},
		'&:hover': {
			backgroundColor: token('color.background.accent.blue.subtler.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.blue.subtler.pressed'),
		},
	},
	teal: {
		color: token('color.text'),
		backgroundColor: token('color.background.accent.teal.subtler'),
	},
	tealClickable: {
		'&:focus-visible': {
			backgroundColor: token('color.background.accent.teal.subtler'),
		},
		'&:hover': {
			backgroundColor: token('color.background.accent.teal.subtler.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.teal.subtler.pressed'),
		},
	},
	lime: {
		color: token('color.text'),
		backgroundColor: token('color.background.accent.lime.subtler'),
	},
	limeClickable: {
		'&:focus-visible': {
			backgroundColor: token('color.background.accent.lime.subtler'),
		},
		'&:hover': {
			backgroundColor: token('color.background.accent.lime.subtler.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.lime.subtler.pressed'),
		},
	},
	purple: {
		color: token('color.text'),
		backgroundColor: token('color.background.accent.purple.subtlest'),
	},
	purpleClickable: {
		'&:focus-visible': {
			backgroundColor: token('color.background.accent.purple.subtlest'),
		},
		'&:hover': {
			backgroundColor: token('color.background.accent.purple.subtlest.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.purple.subtlest.pressed'),
		},
	},
	grey: {
		color: token('color.text'),
		backgroundColor: token('color.background.accent.gray.subtlest'),
	},
	greyClickable: {
		'&:focus-visible': {
			backgroundColor: token('color.background.accent.gray.subtlest'),
		},
		'&:hover': {
			backgroundColor: token('color.background.accent.gray.subtlest.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.gray.subtlest.pressed'),
		},
	},
	popup: {
		width: '330px',
	},
});

export default function DataClassificationLevel({
	testId,
	name,
	tooltip,
	tooltipPosition,
	color = 'GREY',
	iconSize = 'small',
	guideline,
	guidelineADF,
	isDisabled = false,
	isPopupDisabled = false,
	isPopupOpenByDefault = false,
	getPopupStateControllers,
	popupZIndex,
	guidelineRepresentation = 'PLAIN',
	onPopupShown,
	onPopupClose,
	onClick,
	renderPopupComponent,
	popupLabel,
	popupTitleId,
	popupRole,
	popupPlacement = 'bottom-start',
	shouldRenderToParent,
	isIconOnly: isIconOnlyProp = false,
	associatedPoliciesCount,
	spacing = 'default',
}: DataClassificationLevelProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const [isPopupOpen, setIsPopupOpen] = useState(
		isDisabled || isPopupDisabled ? false : isPopupOpenByDefault,
	);
	if (getPopupStateControllers) {
		const getOpenState = () => isPopupOpen;
		const setOpenState = (isOpen: boolean) => setIsPopupOpen(isOpen);
		getPopupStateControllers(getOpenState, setOpenState);
	}

	const isMissingName = !name || name.length === 0;
	const isIconOnly = isMissingName || isIconOnlyProp;

	const nameWithRules = isMissingName
		? ''
		: associatedPoliciesCount
			? name + ' ' + formatMessage(messages.rules, { associatedPoliciesCount })
			: name;
	const nameTruncated = (nameWithRules ?? '').substring(0, nameMaxLength);

	const iconPx: number =
		iconSize === 'small'
			? 12
			: iconSize === 'medium'
				? 18
				: iconSize === 'large'
					? 24
					: iconSize === 'xlarge'
						? 32
						: iconSize === 'xxlarge'
							? 40
							: 12;

	const handleContainerClicked = (): void => {
		if (isPopupDisabled || isDisabled || isMissingName) {
			return;
		}

		if (onPopupShown) {
			onPopupShown();
		}

		setIsPopupOpen(!isPopupOpen);
	};

	const closePopup = () => {
		setIsPopupOpen(false);
	};

	const isClickable = !isPopupDisabled && !isDisabled && !isMissingName;

	const getContainer = (triggerProps = {}) => {
		// @ts-ignore
		const { 'aria-expanded': ariaExpanded, ...restProps } = triggerProps;

		const Container = isClickable ? Pressable : Box;

		return (
			<Container
				role={isClickable ? 'button' : 'presentation'}
				aria-disabled={isDisabled}
				{...(isClickable
					? { 'aria-label': `${formatMessage(messages.dataClassificationLevel)} - ${name}` }
					: {})} // Add aria-label only if clickable as aria-labeledby is not sufficient when popup is collapsed
				xcss={cx(
					...[
						styles.base,
						// Add size styles if not icon only
						...(!isIconOnly ? [spacing === 'expanded' ? styles.expanded : styles.compact] : []),
						// Add disabled styles if disabled
						...(isDisabled ? [styles.disabled] : []),
						// Add color styles
						...(!isDisabled
							? [
									{
										RED: styles.red,
										RED_BOLD: styles.redBold,
										ORANGE: styles.orange,
										YELLOW: styles.yellow,
										GREEN: styles.green,
										BLUE: styles.blue,
										TEAL: styles.teal,
										LIME: styles.lime,
										PURPLE: styles.purple,
										GREY: styles.grey,
									}[color],
								]
							: []),
						// Add clickable styles if clickable
						...(!isDisabled && isClickable
							? [
									styles.clickable,
									{
										RED: styles.redClickable,
										RED_BOLD: styles.redBoldClickable,
										ORANGE: styles.orangeClickable,
										YELLOW: styles.yellowClickable,
										GREEN: styles.greenClickable,
										BLUE: styles.blueClickable,
										TEAL: styles.tealClickable,
										LIME: styles.limeClickable,
										PURPLE: styles.purpleClickable,
										GREY: styles.greyClickable,
									}[color],
								]
							: []),
					],
				)}
				testId={testId}
				padding="space.050"
				onClick={() => {
					if (onClick) {
						onClick();
					}
					handleContainerClicked();
				}}
				{...(isClickable ? triggerProps : restProps)}
			>
				<Flex justifyContent="center" alignItems="center">
					<ClassificationIcon
						width={iconPx}
						height={iconPx}
						color={isDisabled ? disabledColorConfig.iconColor : colorsConfig[color].iconColor}
					/>
				</Flex>
				{!isIconOnly && (
					<Text
						size="small"
						maxLines={1}
						color={color === 'RED_BOLD' ? 'color.text.inverse' : 'color.text'}
						testId="data-classification-level-name"
					>
						{nameTruncated}
					</Text>
				)}
			</Container>
		);
	};

	return isPopupDisabled ? (
		getContainer()
	) : (
		// eslint-disable-next-line @atlaskit/design-system/use-should-render-to-parent
		<Popup
			label={popupLabel}
			titleId={popupTitleId}
			role={popupRole}
			shouldRenderToParent={shouldRenderToParent}
			placement={popupPlacement}
			trigger={(triggerProps) => (
				<Tooltip
					content={isPopupOpen ? null : tooltip}
					hideTooltipOnClick
					position={tooltipPosition}
				>
					{getContainer(triggerProps)}
				</Tooltip>
			)}
			content={() =>
				renderPopupComponent ? (
					renderPopupComponent(closePopup)
				) : (
					<Box
						padding="space.250"
						xcss={styles.popup}
						role="dialog"
						aria-labelledby="data-classification-read-content-name"
						aria-describedby="data-classification-read-content-guideline"
						aria-modal="true"
					>
						<ClassificationView
							name={name}
							guideline={guideline}
							guidelineADF={guidelineADF}
							guidelineRepresentation={guidelineRepresentation}
						/>
					</Box>
				)
			}
			isOpen={isPopupOpen}
			onClose={() => {
				if (onPopupClose) {
					onPopupClose();
				}
				closePopup();
			}}
			zIndex={popupZIndex}
		/>
	);
}
